// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormDatePicker, FormInput, FormSubmitButton, MasterDetailForm, Row } from '@digital/ui';

//Api
import { ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs, validarEnvioNegociacaoMinutaContratual } from '@app/api/sas/sasProposta';

//Files
import { UploadFile, Visibility } from '@app/components';
import { editandoState, modalAcoesIdState, reloadVendorListState, vendorListDocsState } from '../atoms';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalValidarMinutaEnvioNegociacaoMinutaContratual = ({ sasProposta }: Props) => {

    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const setReloadVendorList = useSetRecoilState(reloadVendorListState);
    const editando = useRecoilValue(editandoState);

    const [exibirModal, setExibirModal] = useState(false);
    const [initialValues, setInitialValues] = useState<any>({} as any);

    const vendorListDocs = useRecoilValue(vendorListDocsState);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    useEffect(() => {
        setInitialValues({
            proponente: sasProposta.sasFornecedorOcNome,
            dataValidacaoMinutaEnvioNegociacaoMinutaContratual: sasProposta.dataValidacaoEnvioNegociacaoMinutaContratual
        });

        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSuccess = () => {
        setReloadVendorList((v: boolean) => !v);
        handleCancelAndClose();
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);
        form.append('sasPropostaId', sasProposta.id);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        validarEnvioNegociacaoMinutaContratual(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    handleSuccess();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            });
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            validarEnvioNegociacaoMinutaContratual(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleSuccess();
                    }
                });
        }
    };

    return (
        <Visibility visible={true}>
            <Row align='middle' justify='center' style={{ display: 'flex' }} >

                <Tooltip title="Validar Minuta">
                    <Button icon='business-set_as_resume' onClick={() => { setExibirModal(true); }} />
                </Tooltip>

            </Row>

            <Visibility visible={exibirModal} >

                <Modal
                    title="Validar Minuta"
                    width='780px'
                    visible={exibirModal}
                    onCancel={handleCancel}
                    footer={null}
                >
                    <MasterDetailForm
                        key={`form_ModalValidarMinutaEnvioNegociacaoMinutaContratual_${sasProposta.id}`}
                        initialValues={initialValues}
                        actions={[
                            <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                            <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                        ]}
                        validationSchema={Yup.object().shape({
                            dataValidacaoMinutaEnvioNegociacaoMinutaContratual: Yup.string().required('O campo é obrigatório.'),
                            arquivo: isObjNotEmpty(doc) && doc.anexoValidacaoEnvioNegociacaoMinutaContratual ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                        })}
                    >
                        <Row>
                            <FormInput
                                name="proponente"
                                label='Proponente'
                                value={sasProposta.sasFornecedorOcNome}
                                disabled
                                span={6} />

                            <FormDatePicker
                                label='Data validação da Minuta'
                                name='dataValidacaoMinutaEnvioNegociacaoMinutaContratual'
                                format='DD/MM/YYYY'
                                span={6}
                            />

                        </Row>
                        <br></br>
                        <Row>

                            <UploadFile
                                idDoComponente='arquivo-ModalValidarMinutaEnvioNegociacaoMinutaContratual'
                                type='primary'
                                hasFile={false}
                                hasLabel={true}
                                icon='ui-upload'
                                span={6}
                                onGetArquivo={() => {

                                    if (isObjNotEmpty(doc.anexoValidacaoEnvioNegociacaoMinutaContratual)) {
                                        return Promise.resolve({
                                            id: doc.anexoValidacaoEnvioNegociacaoMinutaContratual.id,
                                            nome: doc.anexoValidacaoEnvioNegociacaoMinutaContratual.nome,
                                            status: "",
                                            statusArquivoId: doc.anexoValidacaoEnvioNegociacaoMinutaContratual.statusArquivoId || "",
                                        } as IArquivo);
                                    }
                                    return Promise.resolve(undefined);

                                }}
                                showDownload={true}
                            />

                        </Row>
                    </MasterDetailForm>
                </Modal>
            </Visibility>
        </Visibility>
    );
};